/**
 * accounting.js — React Query hooks for admin Muhasebe (accounting) screens
 * that are not specific to a single student (banks, muhasebe_tipi taxonomy).
 *
 * Hooks:
 *   useAccountingBanks()      — GET /admin/banks (for the bank_id select)
 *   useMuhasebeTipiOptions()  — GET /admin/definitions?type=muhasebe_tipi
 *   useLawyers()              — GET /admin/lawyers?status=active (FAZ 4.8)
 *
 * Senet yaşam döngüsü mutasyonları (FAZ 4.8 — PromissoryNotesSubTab):
 *   useCreatePromissoryNote / useUpdatePromissoryNote / useDeletePromissoryNote
 *   useCancelPromissoryNote / useSplitPromissoryNote / useMergePromissoryNotes
 *   useLegalTransferPromissoryNotes
 *
 * `muhasebe_tipi` seçenekleri HARD-CODE EDİLMEZ — admin panelinden
 * (AdminDefinitions.jsx) kod değişikliği olmadan kategori eklenebilmesi için
 * her zaman bu endpoint'ten çekilir (bkz. backend/routes/definitions.py).
 *
 * "Ödemeyi Geri Al" mutasyonları (Muhasebe FAZ 6.8 — DebtLedgerSubTab):
 *   useReversePayment(userId)       — POST /admin/payments/{id}/reverse (para
 *                                      hareket ETMEZ, sadece maker-checker
 *                                      talebi açar)
 *   usePendingReversals(userId)     — GET /admin/payment-reversals?status=
 *                                      pending_approval&user_id=...
 *   useApproveReversal(userId)      — POST /admin/payment-reversals/{id}/approve
 *                                      (para BURADA hareket eder)
 *   useRejectReversal(userId)       — POST /admin/payment-reversals/{id}/reject
 *
 * Taksit satır-bazlı tahsilat (F1 fix, fix/tahsilat-taksit-baglama —
 * DebtLedgerSubTab / CollectionForm "taksit modu"):
 *   useCollectInstallment(userId)   — POST /admin/installments/{id}/collect
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { studentKeys } from './students';

export const accountingKeys = {
  banks: ['admin', 'accounting', 'banks'],
  muhasebeTipi: ['admin', 'accounting', 'definitions', 'muhasebe_tipi'],
  lawyers: ['admin', 'accounting', 'lawyers'],
  pendingReversals: (userId) => ['admin', 'accounting', 'payment-reversals', 'pending', userId],
};

// ── useAccountingBanks ────────────────────────────────────────────────────────

/**
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] - GET /admin/banks `require_admin`
 *   ile korunuyor (routes/payments.py::list_banks). Danışman rolünde bu istek
 *   403 döner — FAZ 1 satış sadeleştirme (2026-09-25) kapsamında danışman
 *   ekranlarında (DersEkleKart) hesap seçimi hiç gösterilmediği için çağıran
 *   `enabled: isAdmin` geçerek isteği baştan atlayabilir.
 */
export function useAccountingBanks(options = {}) {
  const { authHeaders } = useAuth();
  const { enabled = true } = options;

  return useQuery({
    queryKey: accountingKeys.banks,
    queryFn: async () => {
      const res = await apiClient.get('/admin/banks', { headers: authHeaders() });
      return res.data.banks || [];
    },
    staleTime: 60_000,
    enabled,
  });
}

// ── useMuhasebeTipiOptions ────────────────────────────────────────────────────

export function useMuhasebeTipiOptions() {
  const { authHeaders } = useAuth();

  return useQuery({
    queryKey: accountingKeys.muhasebeTipi,
    queryFn: async () => {
      const res = await apiClient.get('/admin/definitions?type=muhasebe_tipi', { headers: authHeaders() });
      return (res.data.definitions || []).filter((d) => d.active !== false);
    },
    staleTime: 60_000,
  });
}

// ── useLawyers (FAZ 4.8 — Avukat İşlemleri sekmesi) ───────────────────────────

/**
 * Aktif avukat listesi. GET /admin/lawyers?status=active (routes/legal.py).
 * `lawyer_id` icra devrinde OPSİYONEL bir alandır (backend body'de
 * `lawyer_id: null` kabul eder) — liste boş dönerse form yine gönderilebilir.
 */
export function useLawyers() {
  const { authHeaders } = useAuth();

  return useQuery({
    queryKey: accountingKeys.lawyers,
    queryFn: async () => {
      const res = await apiClient.get('/admin/lawyers?status=active&limit=200', {
        headers: authHeaders(),
      });
      return res.data.lawyers || [];
    },
    staleTime: 60_000,
  });
}

// ── Senet yaşam döngüsü mutasyonları (FAZ 4.8) ────────────────────────────────
//
// Hepsi ADMIN-ONLY uçlar (backend `require_admin`). Başarıda öğrencinin
// `detail` (senet listesi + özet kartları + sekme sayacı) ve `ledger`
// (borç/tahsilat satırları — senet parçalama/birleştirme TAKSİTLERİ de
// değiştirir, S-14) sorguları birlikte invalidate edilir; ikisi de bu
// mutasyonlardan etkilenir.

function useNoteMutation(userId, mutationFn) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: studentKeys.ledger(userId) });
    },
  });
}

/** POST /admin/promissory-notes — manuel senet ekleme (invoice_id ZORUNLU). */
export function useCreatePromissoryNote(userId) {
  const { authHeaders } = useAuth();
  return useNoteMutation(userId, (payload) =>
    apiClient.post('/admin/promissory-notes', payload, { headers: authHeaders() }).then((r) => r.data)
  );
}

/**
 * PUT /admin/promissory-notes/{note_id} — düzenleme + tahsilat.
 * `status` YALNIZCA "collected" olabilir; diğer statülerin kendi uçları var.
 */
export function useUpdatePromissoryNote(userId) {
  const { authHeaders } = useAuth();
  return useNoteMutation(userId, ({ noteId, ...payload }) =>
    apiClient
      .put(`/admin/promissory-notes/${noteId}`, payload, { headers: authHeaders() })
      .then((r) => r.data)
  );
}

/** DELETE /admin/promissory-notes/{note_id} — yalnız `active` senette 200. */
export function useDeletePromissoryNote(userId) {
  const { authHeaders } = useAuth();
  return useNoteMutation(userId, (noteId) =>
    apiClient.delete(`/admin/promissory-notes/${noteId}`, { headers: authHeaders() }).then((r) => r.data)
  );
}

/** POST /admin/promissory-notes/{note_id}/cancel — `reason` ZORUNLU. */
export function useCancelPromissoryNote(userId) {
  const { authHeaders } = useAuth();
  return useNoteMutation(userId, ({ noteId, reason }) =>
    apiClient
      .post(`/admin/promissory-notes/${noteId}/cancel`, { reason }, { headers: authHeaders() })
      .then((r) => r.data)
  );
}

/** POST /admin/promissory-notes/{note_id}/split — `parts`: 2..60 eleman. */
export function useSplitPromissoryNote(userId) {
  const { authHeaders } = useAuth();
  return useNoteMutation(userId, ({ noteId, parts }) =>
    apiClient
      .post(`/admin/promissory-notes/${noteId}/split`, { parts }, { headers: authHeaders() })
      .then((r) => r.data)
  );
}

/** POST /admin/promissory-notes/merge — `new_due_date` ZORUNLU (S-10). */
export function useMergePromissoryNotes(userId) {
  const { authHeaders } = useAuth();
  return useNoteMutation(userId, (payload) =>
    apiClient.post('/admin/promissory-notes/merge', payload, { headers: authHeaders() }).then((r) => r.data)
  );
}

/**
 * POST /admin/promissory-notes/legal-transfer (201) — icraya devir.
 * `debt_amount` GÖNDERİLMEZ: backend senetlerden toplar (aktör tutarı
 * şişiremesin diye body'den okunmuyor).
 */
export function useLegalTransferPromissoryNotes(userId) {
  const { authHeaders } = useAuth();
  return useNoteMutation(userId, (payload) =>
    apiClient
      .post('/admin/promissory-notes/legal-transfer', payload, { headers: authHeaders() })
      .then((r) => r.data)
  );
}

// ── "Ödemeyi Geri Al" (Muhasebe FAZ 6.8) ──────────────────────────────────────

/**
 * POST /admin/payments/{payment_id}/reverse — Body: { reason } (min 10 karakter).
 * 201 ile `payment_reversals` (status="pending_approval") döner — PARA HAREKET
 * ETMEZ, yalnızca onay bekleyen bir talep açar. Başarıda ledger (action
 * kolonundaki "Onay Bekliyor" rozeti) ve bekleyen-talepler listesi invalidate
 * edilir.
 *
 * @param {string} userId
 */
export function useReversePayment(userId) {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ paymentId, reason }) =>
      apiClient
        .post(`/admin/payments/${paymentId}/reverse`, { reason }, { headers: authHeaders() })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentKeys.ledger(userId) });
      qc.invalidateQueries({ queryKey: accountingKeys.pendingReversals(userId) });
    },
  });
}

/**
 * GET /admin/payment-reversals?status=pending_approval&user_id={userId} —
 * öğrenciye özel bekleyen ters kayıt talepleri. `enabled: !!userId` — sayaç
 * (rozet-buton görünürlüğü) popover açılmadan ÖNCE de gerekli olduğu için
 * popover açık/kapalı durumundan BAĞIMSIZ her zaman çalışır.
 *
 * @returns {{ items: [], total: number }}
 */
export function usePendingReversals(userId) {
  const { authHeaders } = useAuth();

  return useQuery({
    queryKey: accountingKeys.pendingReversals(userId),
    queryFn: async () => {
      const res = await apiClient.get('/admin/payment-reversals', {
        headers: authHeaders(),
        params: { status: 'pending_approval', user_id: userId, skip: 0, limit: 50 },
      });
      return res.data || { items: [], total: 0 };
    },
    enabled: !!userId,
    staleTime: 10_000,
  });
}

/**
 * POST /admin/payment-reversals/{reversal_id}/approve — Body yok. 200 ile
 * `status="completed"` + `result_payment_id` döner. PARA BURADA hareket eder.
 * Maker-checker: talebi açan admin onaylayamaz (403 `maker_checker_ihlali`,
 * çağıran component talebi disabled bırakarak proaktif engeller).
 *
 * @param {string} userId
 */
export function useApproveReversal(userId) {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (reversalId) =>
      apiClient
        .post(`/admin/payment-reversals/${reversalId}/approve`, {}, { headers: authHeaders() })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentKeys.ledger(userId) });
      qc.invalidateQueries({ queryKey: accountingKeys.pendingReversals(userId) });
    },
  });
}

/**
 * POST /admin/payment-reversals/{reversal_id}/reject — Body opsiyonel: { note }.
 * 200 ile `status="rejected"` döner. Para hareket etmez ama ödeme satırındaki
 * "Onay Bekliyor" rozeti kalkıp yeniden geri-alma tetikleyicisi görünür hale
 * geldiği için ledger de invalidate edilir.
 *
 * @param {string} userId
 */
export function useRejectReversal(userId) {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ reversalId, note }) =>
      apiClient
        .post(`/admin/payment-reversals/${reversalId}/reject`, note ? { note } : {}, { headers: authHeaders() })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentKeys.ledger(userId) });
      qc.invalidateQueries({ queryKey: accountingKeys.pendingReversals(userId) });
    },
  });
}

// ── useCollectInstallment (F1 fix, fix/tahsilat-taksit-baglama) ──────────────

/**
 * POST /admin/installments/{installment_id}/collect — Borç & Tahsilat
 * ekranında bir taksit SATIRI üzerinden yapılan tahsilat (CollectionForm.jsx
 * "taksit modu"). `useAddManualPayment`'ın AKSİNE `invoice_id`'yi çağıran
 * seçmez — backend zaten `installment.invoice_id`'yi bilir, bu yüzden
 * tahsilat her zaman taksidin GERÇEK faturasına bağlanır (kök çözüm; önceki
 * davranış add-payment'in en eski açık faturayı seçmesiydi, bkz.
 * routes/payments.py::collect_installment docstring'i).
 *
 * Body: `{ amount, payment_method, bank_id (ZORUNLU), net_amount?,
 * muhasebe_tipi?, vat_override_amount?, description? }`. `amount` taksidin
 * (faiz dahil) kalan tutarına ±0.005 eşit olmalı — kısmi tahsilat
 * desteklenmiyor (aksi halde 400 `taksit_tahsilat_tutari_eslesmiyor`).
 *
 * @param {string} userId
 */
export function useCollectInstallment(userId) {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ installmentId, payload }) =>
      apiClient
        .post(`/admin/installments/${installmentId}/collect`, payload, { headers: authHeaders() })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentKeys.ledger(userId) });
      qc.invalidateQueries({ queryKey: studentKeys.detail(userId) });
    },
  });
}
